import * as THREE from 'three';
import { MeshBVH } from 'three-mesh-bvh';
import type {
    VisibleModelEdgeOcclusionContext,
    VisibleModelEdgeOcclusionQuery,
} from '../../../types/sketch/VisibleModelEdgeProjection';
import type { FeatureResultMesh } from '../../mesh/feature';
import { ensureRaycastAccelerationForObjectTree } from '../../raycast-acceleration';

/** 遮挡射线起点离开源边表面的最小世界距离。 */
const MIN_RAY_ORIGIN_OFFSET = 1e-7;

/** 遮挡射线起点偏移相对场景包围盒对角线的比例。 */
const RAY_ORIGIN_OFFSET_SCALE = 1e-7;

/**
 * 基于 MeshBVH 的模型边源点遮挡查询器。
 *
 * 职责：
 * 1. 为当前可见结果体确保 MeshBVH 射线加速；
 * 2. 从源边点沿草图观察侧发射射线；
 * 3. 只要观察侧方向存在任意更靠前的模型三角面，即判定该源点被遮挡。
 *
 * 查询器不负责拓扑边采样或投影分段，避免把网格遮挡细节泄漏到几何服务。
 */
export class MeshBvhModelEdgeOcclusionQuery implements VisibleModelEdgeOcclusionQuery {
    /** 当前参与遮挡查询的结果体。 */
    private readonly resultMeshes: FeatureResultMesh[];

    /** 结果体对应的 MeshBVH。 */
    private readonly meshBvhMap = new Map<FeatureResultMesh, MeshBVH>();

    /** 结果体世界矩阵逆矩阵缓存。 */
    private readonly worldInverseMatrixMap = new Map<FeatureResultMesh, THREE.Matrix4>();

    /** 从模型指向观察侧的单位方向。 */
    private readonly viewDirection: THREE.Vector3;

    /** 当前观察者世界坐标。 */
    private readonly observerPosition: THREE.Vector3;

    /** 射线起点离开源边表面的世界距离。 */
    private readonly rayOriginOffset: number;

    /** 复用的遮挡射线起点。 */
    private readonly rayOrigin = new THREE.Vector3();

    /** 复用的世界空间遮挡射线。 */
    private readonly worldRay = new THREE.Ray();

    /** 复用的结果体局部空间遮挡射线。 */
    private readonly localRay = new THREE.Ray();

    /** 复用的 BVH 命中世界坐标。 */
    private readonly hitWorldPoint = new THREE.Vector3();

    /** 复用的源点到观察者方向向量。 */
    private readonly observerDelta = new THREE.Vector3();

    /**
     * 创建当前投影缓存专用的遮挡查询器。
     *
     * @param context 当前可见结果体与草图观察方向。
     */
    public constructor(context: VisibleModelEdgeOcclusionContext) {
        this.resultMeshes = context.resultMeshes.filter((mesh) => this.isObjectActuallyVisible(mesh));
        this.viewDirection = context.viewDirection.clone().normalize();
        this.observerPosition = context.observerPosition.clone();
        this.rayOriginOffset = this.resolveRayOriginOffset(this.resultMeshes);

        this.resultMeshes.forEach((mesh) => {
            // 结果体会被其它选择链路共享，统一入口以 indirect MeshBVH 保持三角形索引语义稳定。
            ensureRaycastAccelerationForObjectTree(mesh);
            mesh.updateWorldMatrix(true, false);
            const boundsTree = Reflect.get(mesh.geometry, 'boundsTree');
            if (!(boundsTree instanceof MeshBVH)) {
                return;
            }
            this.meshBvhMap.set(mesh, boundsTree);
            this.worldInverseMatrixMap.set(mesh, mesh.matrixWorld.clone().invert());
        });
    }

    /**
     * 判断源模型边上的世界点沿观察侧是否无遮挡。
     *
     * @param worldPoint 源模型边上的世界坐标。
     * @param sourceMesh 源边所属结果体；保留在端口中用于后续按宿主细化容差。
     * @returns 观察侧没有更靠前三角面时返回 true。
     */
    public isPointVisible(worldPoint: THREE.Vector3, sourceMesh: FeatureResultMesh): boolean {
        void sourceMesh;
        if (this.resultMeshes.length === 0 || this.viewDirection.lengthSq() <= 1e-18) {
            return true;
        }

        // 从源边表面向观察侧偏移，避免把源边相邻面在数值容差内的自命中误判成遮挡。
        this.rayOrigin.copy(worldPoint).addScaledVector(this.viewDirection, this.rayOriginOffset);
        const observerDistance = this.observerDelta
            .copy(this.observerPosition)
            .sub(this.rayOrigin)
            .dot(this.viewDirection);
        if (observerDistance <= 0) {
            return true;
        }
        this.worldRay.set(this.rayOrigin, this.viewDirection);

        for (const mesh of this.resultMeshes) {
            const boundsTree = this.meshBvhMap.get(mesh);
            const inverseMatrix = this.worldInverseMatrixMap.get(mesh);
            if (!boundsTree || !inverseMatrix) {
                continue;
            }

            // 遮挡是几何语义，必须强制双面查询，禁止受当前显示材质 FrontSide/BackSide 配置影响。
            this.localRay.copy(this.worldRay).applyMatrix4(inverseMatrix);
            const hit = boundsTree.raycastFirst(this.localRay, THREE.DoubleSide);
            if (!hit) {
                continue;
            }
            this.hitWorldPoint.copy(hit.point).applyMatrix4(mesh.matrixWorld);
            const hitDistance = this.observerDelta
                .copy(this.hitWorldPoint)
                .sub(this.rayOrigin)
                .dot(this.viewDirection);
            if (hitDistance > this.rayOriginOffset * 0.5 && hitDistance <= observerDistance) {
                return false;
            }
        }
        return true;
    }

    /**
     * 按当前结果体包围盒解析稳定的射线起点偏移。
     *
     * @param resultMeshes 当前遮挡结果体。
     * @returns 世界坐标偏移距离。
     */
    private resolveRayOriginOffset(resultMeshes: readonly FeatureResultMesh[]): number {
        const sceneBounds = new THREE.Box3();
        resultMeshes.forEach((mesh) => sceneBounds.expandByObject(mesh, true));
        if (sceneBounds.isEmpty()) {
            return MIN_RAY_ORIGIN_OFFSET;
        }
        const diagonal = sceneBounds.getSize(new THREE.Vector3()).length();
        return Math.max(MIN_RAY_ORIGIN_OFFSET, diagonal * RAY_ORIGIN_OFFSET_SCALE);
    }

    /**
     * 判断对象及其全部父级是否处于可见状态。
     *
     * @param object 待判断对象。
     * @returns 对象真实参与当前显示时返回 true。
     */
    private isObjectActuallyVisible(object: THREE.Object3D): boolean {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (!current.visible) {
                return false;
            }
            current = current.parent;
        }
        return true;
    }
}
